// Types of formats JsBarcode supports that we’ll auto-detect
export type JsBarcodeFormat =
  | 'EAN13'
  | 'UPC' // UPC-A
  | 'EAN8'
  | 'UPCE'
  | 'ITF14'
  | 'EAN5'
  | 'EAN2'
  | 'CODE128'; // safe fallback for non-numeric

export interface DetectOptions {
  /**
   * If an EAN‑13 starts with "0" (i.e., is convertible to UPC‑A),
   * return UPC and drop the leading 0. Default: false (prefer EAN‑13).
   */
  preferUPCWhenEan13StartsWithZero?: boolean;

  /**
   * When true, require valid check digits for 13/12/8-digit inputs.
   * When false (the default), we’ll still choose a format by length,
   * and let JsBarcode compute the check digit if allowed.
   */
  strict?: boolean;
}

export interface DetectResult {
  format: JsBarcodeFormat;
  /** Value to pass to JsBarcode (may be normalized) */
  data: string;
}

/** Public API */
export function detectJsBarcodeFormat(
  raw: string,
  opts: DetectOptions = {}
): DetectResult | null {
  const { preferUPCWhenEan13StartsWithZero = false, strict = false } = opts;

  // Normalize: keep only digits, drop spaces/hyphens commonly found in printed barcodes
  const v = (raw ?? '').replace(/[\s-]/g, '');

  if (!/^\d+$/.test(v)) {
    // Non-numeric: safest is CODE128 which can encode almost anything
    return v ? { format: 'CODE128', data: raw } : null;
  }

  // ITF-14 (GTIN-14 / shipping containers)
  if (v.length === 14) {
    return { format: 'ITF14', data: v };
  }

  // EAN-13 vs UPC-A (12)
  if (v.length === 13) {
    if (!strict || isValidEan13(v)) {
      if (preferUPCWhenEan13StartsWithZero && v.startsWith('0')) {
        // EAN-13 with leading 0 is the UPC-A equivalent
        return { format: 'UPC', data: v.slice(1) }; // drop that leading 0
      }
      return { format: 'EAN13', data: v };
    }
    return null;
  }

  if (v.length === 12) {
    // 12 digits might be a complete UPC-A, OR 12 data digits for EAN‑13
    if (!strict || isValidUpcA(v)) {
      return { format: 'UPC', data: v };
    }
    // If the 12th digit is not a valid UPC check digit, assume it's 12 EAN data digits
    return { format: 'EAN13', data: v }; // JsBarcode will compute the 13th digit
  }

  if (v.length === 11) {
    // 11 UPC data digits -> JsBarcode will compute the check digit
    return { format: 'UPC', data: v };
  }

  // EAN-8 / UPC-E area
  if (v.length === 8) {
    // Distinguishing EAN-8 vs UPC-E by inspection is ambiguous.
    // We choose EAN-8 if it validates; otherwise, fall back to UPC-E if that validates.
    if (!strict || isValidEan8(v)) return { format: 'EAN8', data: v };
    // If you expect a lot of UPC‑E, you could add a UPC‑E validator here.
    // For now we’ll treat invalid 8-digit as unsupported in strict mode.
    return strict ? null : { format: 'EAN8', data: v };
  }

  if (v.length === 7) {
    // 7 EAN-8 data digits -> JsBarcode will compute the check digit
    return { format: 'EAN8', data: v };
  }

  if (v.length === 6) {
    // Likely UPC‑E data (JsBarcode supports 6 or 8)
    return { format: 'UPCE', data: v };
  }

  // Add-on supplements (rarely used standalone)
  if (v.length === 5) return { format: 'EAN5', data: v };
  if (v.length === 2) return { format: 'EAN2', data: v };

  // Unknown numeric length -> unsupported for these encoders
  return null;
}

/* ------------------------ Helpers ------------------------ */

function isValidEan13(code: string): boolean {
  if (!/^\d{13}$/.test(code)) return false;
  return ean13CheckDigit(code.slice(0, 12)) === digit(code[12]);
}

function isValidUpcA(code: string): boolean {
  if (!/^\d{12}$/.test(code)) return false;
  return upcACheckDigit(code.slice(0, 11)) === digit(code[11]);
}

function isValidEan8(code: string): boolean {
  if (!/^\d{8}$/.test(code)) return false;
  return ean8CheckDigit(code.slice(0, 7)) === digit(code[7]);
}

function ean13CheckDigit(d12: string): number {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const n = digit(d12[i]);
    sum += i % 2 === 0 ? n : 3 * n;
  }
  return (10 - (sum % 10)) % 10;
}

function upcACheckDigit(d11: string): number {
  // 1st,3rd,5th,... (index 0,2,4,6,8,10) *3  +  2nd,4th,6th,... (index 1,3,5,7,9)
  let odd = 0,
    even = 0;
  for (let i = 0; i < 11; i++) {
    const n = digit(d11[i]);
    if (i % 2 === 0) odd += n;
    else even += n;
  }
  const total = odd * 3 + even;
  return (10 - (total % 10)) % 10;
}

function ean8CheckDigit(d7: string): number {
  // 1st,3rd,5th,7th *3  +  2nd,4th,6th
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    const n = digit(d7[i]);
    sum += i % 2 === 0 ? 3 * n : n;
  }
  return (10 - (sum % 10)) % 10;
}

function digit(ch: string): number {
  return ch.charCodeAt(0) - 48; // faster than Number(ch)
}
